import { useNavigate, useSearch } from '@tanstack/react-router';

import { useDrawer } from '../cdl/Drawer/useDrawer.hook';
import { IconButton } from '../cdl/IconButton/IconButton';
import { CenteredPagination } from '../cdl/Pagination/CenteredPagination';
import { TableBuilder } from '../cdl/TableBuilder/TableBuilder';
import { TableBuilderColumn } from '../cdl/TableBuilder/TableBuilderColumn';
import { DeleteIconButton } from '../common/buttons/DeleteIconButton';
import { AdvancedError } from '../common/Error/AdvancedError';
import { formatPort } from '../common/helpers/formatPort.helper';
import { formatVessel } from '../common/helpers/formatVessel.helper';
import { translate } from '../common/helpers/translate.helper';
import { useEnums } from '../common/hooks/useEnums';
import { useRole } from '../common/hooks/useRole';
import { IconPencil } from '../common/icons/cdl/Pencil';
import { IconStar } from '../common/icons/cdl/Star';
import { ListingWithMore } from '../common/ListingWithMore/ListingWithMore';
import { LoadingIndicator } from '../common/LoadingIndicator/LoadingIndicator';

import { TableEmptyState } from '../common/TableEmptyState/TableEmptyState';

import { Port } from '../port/Port';

import { Box } from '../common/ui/Box';

import { EditKeyPortForm } from './EditKeyPortForm';
import { useKeyPortDelete } from './useKeyPortDelete';
import { useKeyPortPagination } from './useKeyPortPagination';

export const KeyPortOverviewTable = () => {
    const role = useRole();
    const { getHumanReadableValue } = useEnums();
    const { push, pop } = useDrawer();

    const { mutate: deleteKeyPort } = useKeyPortDelete();

    const navigate = useNavigate({ from: '/key-ports' });
    const search = useSearch({ strict: false });

    const { data, isPending, isError, pageTotal } = useKeyPortPagination({
        page: search.page,
        searchQuery: search.searchQuery,
        customerGroupIds: search.customerGroupIds,
    });

    if (isPending) {
        return <LoadingIndicator />;
    }

    if (isError) {
        return <AdvancedError />;
    }

    if (!data.content.length) {
        return <TableEmptyState Icon={IconStar} text={translate('keyPorts.emptyList')} />;
    }

    const openEditKeyPortModal = (keyPortId) => {
        const drawerId = push({
            header: { title: translate('keyPorts.editKeyPort') },
            content: <EditKeyPortForm keyPortId={keyPortId} onSuccess={() => pop(drawerId)} />,
        });
    };

    return (
        <div>
            <TableBuilder data={data.content}>
                <TableBuilderColumn header={translate('keyPorts.port')} width="20%">
                    {(keyPort) => <Port port={keyPort.port} country={false} showTooltip />}
                </TableBuilderColumn>
                <TableBuilderColumn header={translate('keyPorts.country')}>
                    {(keyPort) => keyPort.port.country.name}
                </TableBuilderColumn>
                <TableBuilderColumn header={translate('keyPorts.region')}>
                    {(keyPort) => getHumanReadableValue('region', keyPort.port.country.region)}
                </TableBuilderColumn>
                <TableBuilderColumn header={translate('keyPorts.vesselLimitations')} width="20%">
                    {(keyPort) => {
                        if (keyPort.vesselLimitationsVessels?.length) {
                            const combinedLimitations = keyPort.vesselLimitationsVessels?.map((vessel) =>
                                formatVessel({ vessel })
                            );
                            return <ListingWithMore>{combinedLimitations}</ListingWithMore>;
                        }
                    }}
                </TableBuilderColumn>
                {role.isSuperAdmin() ? (
                    <TableBuilderColumn header={translate('keyPorts.group')}>
                        {(keyPort) => keyPort.customerGroup.name}
                    </TableBuilderColumn>
                ) : null}

                {role.hasWriteRights() ? (
                    <TableBuilderColumn header="" width="110px">
                        {(keyPort) => (
                            <Box display="flex" gap="4px">
                                <IconButton
                                    Icon={IconPencil}
                                    onClick={() => openEditKeyPortModal(keyPort.id)}
                                    tooltipLabel={translate('keyPorts.editKeyPort')}
                                />
                                <DeleteIconButton
                                    onDeleteConfirm={() => deleteKeyPort(keyPort.id)}
                                    popupHeadline={translate('keyPorts.deleteButton.headline')}
                                    popupText={translate('keyPorts.deleteButton.text')}
                                    tooltip={translate('keyPorts.deleteButton.headline')}
                                    itemToDelete={formatPort({
                                        port: keyPort.port,
                                        country: false,
                                    })}
                                />
                            </Box>
                        )}
                    </TableBuilderColumn>
                ) : null}
            </TableBuilder>

            <CenteredPagination
                pageTotal={pageTotal}
                currentPage={search.page}
                onPageChange={(page) => {
                    navigate({ search: (previousSearch) => ({ ...previousSearch, page }) });
                }}
            />
        </div>
    );
};
